import portalApi, { PORTAL_ACCESS_TOKEN_KEY, PORTAL_REFRESH_TOKEN_KEY } from './portalApi';

export interface CustomerProfile {
  id: string;
  email: string;
  firstName?: string | null;
  lastName?: string | null;
  companyId: string;
  partnerId: string;
  partnerName: string;
  companyName: string;
  gstin?: string | null;
  phone?: string | null;
  partnerEmail?: string | null;
  partnerAddress?: string | null;
  locationCity?: string | null;
  partnerPincode?: string | null;
  paymentTerm?: string | null;
  accountStatus: string;
}

export interface CustomerAuthResponse {
  accessToken: string;
  refreshToken: string;
  customer: CustomerProfile;
}

export interface CustomerLoginCredentials {
  email: string;
  password: string;
}

function storePortalTokens(accessToken: string, refreshToken: string) {
  localStorage.setItem(PORTAL_ACCESS_TOKEN_KEY, accessToken);
  localStorage.setItem(PORTAL_REFRESH_TOKEN_KEY, refreshToken);
}

function clearPortalTokens() {
  localStorage.removeItem(PORTAL_ACCESS_TOKEN_KEY);
  localStorage.removeItem(PORTAL_REFRESH_TOKEN_KEY);
}

class CustomerAuthService {
  isAuthenticated(): boolean {
    return !!localStorage.getItem(PORTAL_ACCESS_TOKEN_KEY);
  }

  async login(credentials: CustomerLoginCredentials): Promise<CustomerAuthResponse> {
    const { data } = await portalApi.post<CustomerAuthResponse>('/customer-auth/login', credentials);
    storePortalTokens(data.accessToken, data.refreshToken);
    return data;
  }

  async forgotPassword(email: string): Promise<{ message: string }> {
    const { data } = await portalApi.post<{ message: string }>('/customer-auth/forgot-password', {
      email,
    });
    return data;
  }

  async resetPassword(token: string, newPassword: string): Promise<{ message: string }> {
    const { data } = await portalApi.post<{ message: string }>('/customer-auth/reset-password', {
      token,
      newPassword,
    });
    return data;
  }

  async getProfile(): Promise<CustomerProfile> {
    const { data } = await portalApi.get<CustomerProfile>('/customer-auth/me');
    return data;
  }

  async logout(): Promise<void> {
    const refreshToken = localStorage.getItem(PORTAL_REFRESH_TOKEN_KEY);
    try {
      if (refreshToken) {
        await portalApi.post('/customer-auth/logout', { refreshToken });
      }
    } finally {
      clearPortalTokens();
    }
  }

  clearSession(): void {
    clearPortalTokens();
  }
}

const customerAuthService = new CustomerAuthService();
export default customerAuthService;
